<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import WhatsappWidgetAPI from 'dashboard/api/whatsappWidget';
import ButtonV4 from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ColorPicker from 'dashboard/components-next/colorpicker/ColorPicker.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();

// Official WhatsApp brand glyph, kept in sync with Whatsapp::WidgetScriptService::ICON_PATHS
const WHATSAPP_ICON_PATH =
  'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z';

const widget = reactive({
  enabled: false,
  phone_number: '',
  greeting_text: '',
  button_label: '',
  position: 'right',
  color: '#25D366',
  show_on_mobile: true,
});

const script = ref('');
const isLoading = ref(false);
const isSaving = ref(false);

const positionOptions = computed(() => [
  { value: 'right', label: t('INBOX_MGMT.WHATSAPP_WIDGET.POSITIONS.RIGHT') },
  { value: 'left', label: t('INBOX_MGMT.WHATSAPP_WIDGET.POSITIONS.LEFT') },
]);

const previewLink = computed(() => {
  const number = String(widget.phone_number || '').replace(/\D/g, '');
  const text = widget.greeting_text
    ? `?text=${encodeURIComponent(widget.greeting_text)}`
    : '';
  return `https://wa.me/${number}${text}`;
});

const applyResponse = data => {
  Object.assign(widget, {
    enabled: data.enabled,
    phone_number: data.phone_number || '',
    greeting_text: data.greeting_text || '',
    button_label: data.button_label || '',
    position: data.position || 'right',
    color: data.color || '#25D366',
    show_on_mobile: data.show_on_mobile !== false,
  });
  script.value = data.script;
};

const fetchWidget = async () => {
  isLoading.value = true;
  try {
    const { data } = await WhatsappWidgetAPI.get(props.inbox.id);
    applyResponse(data);
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('INBOX_MGMT.WHATSAPP_WIDGET.FETCH_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};

const saveWidget = async () => {
  isSaving.value = true;
  try {
    const { data } = await WhatsappWidgetAPI.update(props.inbox.id, {
      ...widget,
    });
    applyResponse(data);
    useAlert(t('INBOX_MGMT.WHATSAPP_WIDGET.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('INBOX_MGMT.WHATSAPP_WIDGET.UPDATE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

const copyScript = async () => {
  await copyTextToClipboard(script.value);
  useAlert(t('INBOX_MGMT.WHATSAPP_WIDGET.COPIED'));
};

onMounted(fetchWidget);
</script>

<template>
  <div class="grid grid-cols-1 gap-8 lg:grid-cols-2">
    <div class="flex flex-col gap-5">
      <div class="flex flex-col gap-1">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_WIDGET.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.WHATSAPP_WIDGET.DESCRIPTION') }}
        </p>
      </div>

      <div class="flex items-center justify-between">
        <div class="flex flex-col">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.WHATSAPP_WIDGET.ENABLE') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.WHATSAPP_WIDGET.ENABLE_HELP') }}
          </span>
        </div>
        <Switch v-model="widget.enabled" />
      </div>

      <Input
        v-model="widget.phone_number"
        :label="$t('INBOX_MGMT.WHATSAPP_WIDGET.PHONE_NUMBER')"
        :message="$t('INBOX_MGMT.WHATSAPP_WIDGET.PHONE_NUMBER_HELP')"
      />

      <Input
        v-model="widget.button_label"
        :label="$t('INBOX_MGMT.WHATSAPP_WIDGET.BUTTON_LABEL')"
      />

      <Input
        v-model="widget.greeting_text"
        :label="$t('INBOX_MGMT.WHATSAPP_WIDGET.GREETING')"
        :message="$t('INBOX_MGMT.WHATSAPP_WIDGET.GREETING_HELP')"
      />

      <div class="flex items-end gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.WHATSAPP_WIDGET.POSITION') }}
          </label>
          <Select v-model="widget.position" :options="positionOptions" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.WHATSAPP_WIDGET.COLOR') }}
          </label>
          <ColorPicker v-model="widget.color" />
        </div>
      </div>

      <div class="flex items-center justify-between">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_WIDGET.SHOW_ON_MOBILE') }}
        </span>
        <Switch v-model="widget.show_on_mobile" />
      </div>

      <div>
        <ButtonV4
          :is-loading="isSaving"
          :disabled="isLoading || isSaving"
          :label="$t('INBOX_MGMT.WHATSAPP_WIDGET.SAVE')"
          @click="saveWidget"
        />
      </div>
    </div>

    <div class="flex flex-col gap-4">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.WHATSAPP_WIDGET.PREVIEW') }}
      </h3>

      <div
        class="relative h-64 overflow-hidden border rounded-xl border-n-weak bg-n-alpha-1"
      >
        <div class="p-4 text-xs text-n-slate-10">
          {{ $t('INBOX_MGMT.WHATSAPP_WIDGET.PREVIEW_HINT') }}
        </div>
        <a
          :href="previewLink"
          target="_blank"
          rel="noopener noreferrer"
          class="absolute bottom-5 inline-flex items-center justify-center text-sm font-semibold text-white no-underline rounded-full shadow-lg"
          :class="[
            widget.position === 'left' ? 'left-5' : 'right-5',
            widget.button_label ? 'gap-2 px-[18px] py-3.5' : 'p-3.5',
          ]"
          :style="{ backgroundColor: widget.color }"
        >
          <svg viewBox="0 0 24 24" class="size-5 shrink-0" fill="currentColor">
            <path :d="WHATSAPP_ICON_PATH" />
          </svg>
          <span v-if="widget.button_label">{{ widget.button_label }}</span>
        </a>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_WIDGET.SCRIPT') }}
        </span>
        <p class="text-xs text-n-slate-11">
          {{ $t('INBOX_MGMT.WHATSAPP_WIDGET.SCRIPT_HELP') }}
        </p>
        <div
          class="p-3 overflow-x-auto font-mono text-xs break-all rounded-lg bg-n-alpha-2 text-n-slate-12"
        >
          {{ script }}
        </div>
        <div>
          <ButtonV4
            sm
            faded
            slate
            icon="i-lucide-clipboard"
            :label="$t('INBOX_MGMT.WHATSAPP_WIDGET.COPY')"
            @click="copyScript"
          />
        </div>
      </div>
    </div>
  </div>
</template>
